import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Namespaces loaded at startup (Phase 00: only common + auth).
// Remaining namespaces are lazy-loaded in Phase 07 when all strings are wired up.
import enCommon from '@nexdel/i18n/en/common.json';
import enAuth from '@nexdel/i18n/en/auth.json';
import frCommon from '@nexdel/i18n/fr/common.json';
import frAuth from '@nexdel/i18n/fr/auth.json';

export const NAMESPACES = ['common', 'auth'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NS = 'common';
export const SUPPORTED_LANGS = ['en', 'fr'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/**
 * Resolve the language to use for this session, in priority order:
 *   1. User profile language (from the DB)
 *   2. localStorage (persisted from a previous session)
 *   3. Browser language
 *   4. Organization default language
 *   5. 'en' fallback
 */
export function resolveLanguage(
  profileLanguage: string | null | undefined,
  orgDefaultLanguage: string | null | undefined
): SupportedLang {
  const candidates = [
    profileLanguage,
    localStorage.getItem('i18nextLng'),
    navigator.language,
    orgDefaultLanguage,
    'en',
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const lang = candidate.slice(0, 2).toLowerCase();
    if ((SUPPORTED_LANGS as readonly string[]).includes(lang)) {
      return lang as SupportedLang;
    }
  }

  return 'en';
}

let initialized = false;

export async function initI18n(initialLang?: SupportedLang): Promise<void> {
  if (initialized) return;
  initialized = true;

  const lang = initialLang ?? resolveLanguage(null, null);

  await i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: lang,
      fallbackLng: 'en',
      defaultNS: DEFAULT_NS,
      ns: NAMESPACES,
      resources: {
        en: { common: enCommon, auth: enAuth },
        fr: { common: frCommon, auth: frAuth },
      },
      interpolation: { escapeValue: false },
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
      },
    });
}

export { i18next as i18n };
